import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { HelpCircle, PlayCircle } from "lucide-react";

import { useMediaPipePose } from "@/lib/pose/useMediaPipePose";
import {
  SetupGuideCarrusel,
  hasSeenSetupGuide,
  markSetupGuideAsSeen,
} from "@/app/components/paciente/SetupGuideCarrusel";
import { VideoTutorialModal } from "@/app/components/sessions/VideoTutorialModal";
import { YoutubeThumbnail } from "@/app/components/patients/YoutubeThumbnail";
import { buildPoseAnalysis } from "@/lib/pose/buildPoseAnalysis";
import { drawSkeleton } from "@/lib/pose/drawSkeleton";
import { getVisibleVideoRect, type ObjectFit } from "@/lib/pose/videoRect";
import {
  useAudioFeedback,
  type CueName,
} from "@/lib/audio/useAudioFeedback";
import {
  createEvaluator,
  getExerciseSchema,
} from "@/lib/pose/exerciseSchemas";
import { getViolationLabel } from "@/lib/pose/getViolationLabel";
import { useGuardarSesionCompletada } from "@/lib/supabase/queries/useGuardarSesionCompletada";
import type {
  BaselineSnapshot,
  EvaluationResult,
  Evaluator,
  PoseResult,
  RepResult,
} from "@/lib/pose/types";
import {
  SesionCamaraUI,
  type FeedbackItem,
  type LoadingState,
  type SerieMode,
} from "@/app/components/sessions/SesionCamaraUI";
import { DetectorDebugHUD } from "@/app/components/debug/DetectorDebugHUD";
import type { ResumenSesion } from "@/app/pages/SesionCompletada";

export interface SesionCamaraProps {
  /** UUID de la fila en `sesiones` de Supabase, creada al click del paciente
   *  en HomePaciente (ver useCrearSesion). El wrapper de routes.tsx valida
   *  que la sesión exista y esté en estado consumible antes de renderizar. */
  sessionId: string;
  /** ID del ejercicio del catálogo (texto, ej. "rot_externa_banda"). Viene
   *  del wrapper que ya lo extrajo de `sesiones.ejercicio_id`. */
  exerciseId: string;
}

export function SesionCamara({ sessionId, exerciseId }: SesionCamaraProps) {
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const evaluatorRef = useRef<Evaluator | null>(null);
  // Fit efectivo del <video> (contain/cover). Re-leído del computed style
  // en cada resize (breakpoint lg cambia object-cover → object-contain).
  // Se lee UNA vez por resize en lugar de por frame, evita hits repetidos
  // al layout tree desde handlePose (que corre a 30-60fps).
  const fitRef = useRef<ObjectFit>("cover");

  // Audio feedback — 27 cues del guion (sección agnóstica). Precarga al
  // click "Empezar sesión" (user gesture, satisface auto-play policy).
  const audio = useAudioFeedback();
  // playRef expuesto a handlePose (useCallback([])) sin invalidar el
  // hook de MediaPipe. Ref al play + a schema + a repsObjetivo para el
  // dispatch de cue_num / cue_una_mas / cue_ultima / cue_vas_bien /
  // cue_sin_deteccion desde adentro del frame loop.
  const audioPlayRef = useRef(audio.play);
  useEffect(() => {
    audioPlayRef.current = audio.play;
  }, [audio.play]);
  // Debouncer de LOW-vis: pérdida sostenida durante N frames dispara
  // cue_sin_deteccion UNA vez. Se resetea cuando visibility se recupera
  // (edge trigger). Sin este debounce, fluctuaciones de 1 frame
  // spamearían el cue.
  const lowVisFramesRef = useRef(0);
  const lowVisAnnouncedRef = useRef(false);
  // 60 frames @ 30fps ≈ 2s. Suficientemente largo para no disparar por
  // oclusiones breves, corto para que el paciente reciba feedback si
  // sigue fuera del frame.
  const LOW_VIS_THRESHOLD_FRAMES = 60;
  // Buffer de FPS y timestamp de inicio (post-countdown) para métricas de
  // sesión. Refs en lugar de state — pushar al fps buffer 30 veces/seg como
  // useState dispararía 30 re-renders/seg.
  const fpsHistoryRef = useRef<number[]>([]);
  const tiempoInicioRef = useRef<number | null>(null);

  const schema = useMemo(() => getExerciseSchema(exerciseId), [exerciseId]);
  // Ref al schema para leer repsObjetivo desde handlePose (useCallback([])).
  const schemaRef = useRef(schema);
  useEffect(() => {
    schemaRef.current = schema;
  }, [schema]);

  // Instancia del evaluador, persistente entre renders. La factory
  // despacha al evaluator correcto según schema.type (legacy
  // ExerciseEvaluator para oscillating_angle, SquatEvaluator para
  // squat_phases). Sin esto SesionCamara solo soporta rot_externa_banda.
  useEffect(() => {
    if (schema) evaluatorRef.current = createEvaluator(schema);
  }, [schema]);

  const [started, setStarted] = useState(false);
  const [showDebug, setShowDebug] = useState(false);
  // Setup guide: auto-trigger la primera vez (flag en localStorage), y
  // re-acceso vía ícono permanente en el pre-session gate. El flag se
  // setea en onClose — saltar también cierra para siempre. El ícono
  // permanente abre el modal sin tocar el flag (re-acceso).
  const [setupGuideOpen, setSetupGuideOpen] = useState<boolean>(
    () => !hasSeenSetupGuide(),
  );
  // Sprint 3 spec-medicion Bloque 1: modal opcional con iframe de YouTube
  // del video tutorial del ejercicio. Se abre desde el botón "Ver cómo se
  // hace" del pre-session gate. Solo se renderea el botón si el schema
  // tiene video_url.
  const [videoTutorialOpen, setVideoTutorialOpen] = useState(false);
  const [currentSerie, setCurrentSerie] = useState(1);
  const [evaluation, setEvaluation] = useState<EvaluationResult | null>(null);
  const [completedReps, setCompletedReps] = useState<RepResult[][]>([[]]);
  // Estado del ciclo de la serie. Arranca en 'idle' cuando el paciente clickea
  // "Empezar sesión" del pre-session gate. El detector recibe frames cuando
  // mode === 'active' (procesando reps) o mode === 'calibracion' (capturando
  // baseline para schemas que lo requieren — sentadilla).
  const [mode, setMode] = useState<SerieMode>("idle");
  const [countdownValue, setCountdownValue] = useState<number | null>(null);

  // Estado de la captura de baseline. Solo relevante cuando schema tiene
  // baseline (sentadilla). Para rot externa estos valores quedan en defaults.
  const [calibracionProgreso, setCalibracionProgreso] = useState(0);
  const [calibracionIntentos, setCalibracionIntentos] = useState(0);
  // BaselineSnapshot capturado al cierre del gate OK. C4 lo va a leer desde
  // acá para persistir en pose_analysis V2. Por ahora vive en memoria de la
  // sesión y se pierde en refresh (deuda conocida).
  const baselineRef = useRef<BaselineSnapshot | null>(null);
  // prevProgressRef detecta el flip de progreso > 0 → 0, que es el signal
  // interno del SquatEvaluator de que el gate falló (reseteó el capture).
  const prevProgressRef = useRef(0);

  // sessionRef da acceso estable al currentSerie y al mode actual desde dentro
  // de handlePose (que es useCallback([]) por el stale-closure intencional
  // del hook useMediaPipePose).
  const sessionRef = useRef<{ currentSerie: number; mode: SerieMode }>({
    currentSerie: 1,
    mode: "idle",
  });
  useEffect(() => {
    sessionRef.current.currentSerie = currentSerie;
  }, [currentSerie]);
  useEffect(() => {
    sessionRef.current.mode = mode;
  }, [mode]);

  const handlePose = useCallback((pose: PoseResult) => {
    // 1) Skeleton drawing — SIEMPRE que llegue un frame, sin importar el
    //    mode. Feedback visual "la IA te está viendo" también en idle/
    //    counting/rest/finished (mientras la cámara está corriendo).
    //    Corre ANTES del early return del evaluator para eso.
    const canvas = canvasRef.current;
    const container = containerRef.current;
    const video = videoRef.current;
    if (canvas && container && video && video.videoWidth > 0) {
      const ctx = canvas.getContext("2d");
      if (ctx) {
        const dpr = window.devicePixelRatio || 1;
        const cw = container.clientWidth;
        const ch = container.clientHeight;
        const rect = getVisibleVideoRect({
          videoWidth: video.videoWidth,
          videoHeight: video.videoHeight,
          containerWidth: cw,
          containerHeight: ch,
          fit: fitRef.current,
        });
        // setTransform en lugar de scale() para no acumular entre frames.
        // Todo el drawing usa coords CSS (px del contenedor).
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, cw, ch);
        drawSkeleton({ ctx, keypoints: pose.keypoints, rect });
      }
    }

    // 2) Evaluator — solo procesa frames durante 'active' (reps) o
    //    'calibracion' (captura de baseline). Durante idle/counting/rest/
    //    finished la cámara sigue corriendo pero el evaluator no recibe nada.
    const currentMode = sessionRef.current.mode;
    if (currentMode !== "active" && currentMode !== "calibracion") return;
    if (!evaluatorRef.current) return;
    // Push fps al buffer para promedio de sesión al cierre. Mutación al ref,
    // sin re-render.
    fpsHistoryRef.current.push(pose.fps);
    const result = evaluatorRef.current.evaluate(pose);
    setEvaluation(result);

    if (currentMode === "calibracion") {
      // Trackear progreso de la captura. El SquatEvaluator emite
      // baselineProgress en 0..1 mientras captura, y luego baselineSnapshot
      // poblado en el frame que pasa el gate. Si el gate falla, el evaluator
      // resetea internamente (progress vuelve a 0) sin avisarnos — lo
      // detectamos por el flip prevProgress > 0.5 → 0.
      const progress = result.baselineProgress ?? 0;
      const snapshot = result.baselineSnapshot;
      const prev = prevProgressRef.current;
      if (snapshot) {
        baselineRef.current = snapshot;
        setCalibracionProgreso(1);
        prevProgressRef.current = 0;
        // Transición a counting (3-2-1 antes de empezar reps).
        setMode("counting");
        setCountdownValue(3);
      } else {
        setCalibracionProgreso(progress);
        if (prev > 0.5 && progress === 0) {
          // Gate failed — el evaluator reinició el capture internamente.
          // Contamos el intento; el effect que mira calibracionIntentos
          // dispara la transición a calibracion_fallida si llega a 3.
          setCalibracionIntentos((n) => n + 1);
        }
        prevProgressRef.current = progress;
      }
      return;
    }

    if (result.completedRep) {
      const completed = result.completedRep;
      setCompletedReps((prev) => {
        const copy = prev.map((s) => [...s]);
        copy[sessionRef.current.currentSerie - 1].push(completed);
        return copy;
      });

      // Audio-feedback rep válida: cue_num_<N> + encadenado opcional.
      // El schema.repsObjetivo se lee de schemaRef (stable, actualizado en
      // cada render). cue_num_1..15 → si N > 15, silencio (sin cue extra).
      if (completed.valid && schemaRef.current) {
        const N = completed.n;
        const objetivo = schemaRef.current.repsObjetivo;
        if (N >= 1 && N <= 15) {
          audioPlayRef.current(`cue_num_${N}` as CueName);
        }
        // Encadenado a 800ms — cue_num dura ~500ms, deja margen para
        // el cue extra sin que se pise al inicio.
        if (N === objetivo - 1) {
          setTimeout(() => audioPlayRef.current("cue_una_mas"), 800);
        } else if (N > 0 && N % 5 === 0 && N < objetivo - 1) {
          setTimeout(() => audioPlayRef.current("cue_vas_bien"), 800);
        }
        // N === objetivo: NO disparamos cue_ultima. Es redundante con
        // cue_serie_completa (dispara ~inmediato al setMode('rest')) y
        // encadenado +800ms llegaría DESPUÉS del serie_completa, invertido.
        // El cue_serie_completa está delayed en el mode-transition effect
        // para dar aire a cue_num_<objetivo>.
      }
    }

    // Audio-feedback pérdida sostenida de keypoints: debounce 60 frames
    // (~2s @ 30fps). Edge trigger: dispara una vez, se resetea cuando
    // visibility se recupera.
    const isLowVis = result.debug?.visibility === "LOW";
    if (isLowVis) {
      lowVisFramesRef.current++;
      if (
        lowVisFramesRef.current >= LOW_VIS_THRESHOLD_FRAMES &&
        !lowVisAnnouncedRef.current
      ) {
        audioPlayRef.current("cue_sin_deteccion");
        lowVisAnnouncedRef.current = true;
      }
    } else {
      lowVisFramesRef.current = 0;
      lowVisAnnouncedRef.current = false;
    }
  }, []);

  const { pose, isLoading, error, start } = useMediaPipePose({
    videoRef,
    enabled: started,
    onPose: handlePose,
  });

  // Setup del canvas para el skeleton drawing: dimensiones intrínsecas
  // sincronizadas con el contenedor + DPR para HiDPI. ResizeObserver
  // re-corre cuando cambia el tamaño (rotación mobile, viewport resize,
  // cambio de breakpoint lg). También re-lee el object-fit del <video>
  // porque puede cambiar entre breakpoints (mobile cover → desktop contain).
  useEffect(() => {
    if (!started) return;
    const canvas = canvasRef.current;
    const container = containerRef.current;
    const video = videoRef.current;
    if (!canvas || !container) return;

    const updateDims = () => {
      const dpr = window.devicePixelRatio || 1;
      canvas.width = container.clientWidth * dpr;
      canvas.height = container.clientHeight * dpr;
      if (video) {
        const objectFit = window.getComputedStyle(video).objectFit;
        fitRef.current = objectFit === "contain" ? "contain" : "cover";
      }
    };

    updateDims();
    const ro = new ResizeObserver(updateDims);
    ro.observe(container);
    return () => ro.disconnect();
  }, [started]);

  const repCount = evaluation?.repCount ?? 0;
  const feedback: FeedbackItem[] = evaluation
    ? evaluation.ruleEvaluations
        .filter((r) => !r.passed)
        .map((r) => ({ id: r.violationId, message: r.feedback, passed: false }))
    : [];

  // Countdown — decrementa cada 1s. Cuando llega a 0, transiciona a 'active'
  // y limpia el counter. Si el modo cambia mid-countdown (ej. salir), el
  // cleanup de setTimeout evita ticks fantasma.
  //
  // OJO deps: usar audioPlayRef.current (no audio.play), y NO poner `audio`
  // en el array. El return de useAudioFeedback() es un objeto nuevo cada
  // render — con `audio` en deps, este effect corría a 30-60fps (cada
  // setEvaluation), re-creando el setTimeout(1000) cada 16ms → nunca
  // llegaba a completar → countdown clavado en 3. En pestaña background
  // el requestAnimationFrame se pausa y el bug "desaparece" (por eso
  // Ale reportó "corre en background, se clava en visible").
  useEffect(() => {
    if (mode !== "counting" || countdownValue === null) return;
    if (countdownValue === 0) {
      // Audio: 'cue_empeza' al transicionar counting → active.
      audioPlayRef.current("cue_empeza");
      // Capturar inicio de sesión la PRIMERA vez que arranca activo
      // (no resetear entre series — duration_sec es el total de la sesión).
      if (tiempoInicioRef.current === null) {
        tiempoInicioRef.current = Date.now();
      }
      setMode("active");
      setCountdownValue(null);
      return;
    }
    // Audio: 'cue_countdown_3/2/1' sincronizado con el número visible.
    if (countdownValue >= 1 && countdownValue <= 3) {
      audioPlayRef.current(`cue_countdown_${countdownValue}` as CueName);
    }
    const t = setTimeout(() => {
      setCountdownValue((v) => (v === null ? null : v - 1));
    }, 1000);
    return () => clearTimeout(t);
  }, [mode, countdownValue]);

  // Audio de mode transitions (calibracion, rest, finished). Usa un ref
  // al mode previo para detectar transiciones específicas.
  // Mismo cuidado con deps que el effect anterior — audioPlayRef.current
  // en lugar de audio.play, y sin `audio` en el array (evita re-runs por
  // cada frame que puedan cancelar los setTimeout de 700ms).
  const prevModeRef = useRef(mode);
  useEffect(() => {
    const prev = prevModeRef.current;
    if (mode === "calibracion" && prev !== "calibracion") {
      audioPlayRef.current("cue_calibrando");
    } else if (mode === "rest" && prev === "active") {
      // Delay 700ms para no pisar cue_num_<objetivo> que dispara desde
      // handlePose ~simultáneo con esta transición. Con el delay:
      // "Doce" (~500ms) termina antes de "Serie completa" arranca.
      const t = setTimeout(() => audioPlayRef.current("cue_serie_completa"), 700);
      prevModeRef.current = mode;
      return () => clearTimeout(t);
    } else if (mode === "finished" && prev === "active") {
      // Mismo problema en la última rep de la última serie — delayed
      // por la misma razón.
      const t = setTimeout(() => audioPlayRef.current("cue_sesion_completa"), 700);
      prevModeRef.current = mode;
      return () => clearTimeout(t);
    }
    prevModeRef.current = mode;
  }, [mode]);

  // Auto-transition: cuando se completan las reps de la serie en activo,
  // pasar a 'rest' (si quedan series) o 'finished' (si fue la última).
  useEffect(() => {
    if (mode !== "active" || !schema) return;
    if (repCount >= schema.repsObjetivo) {
      if (currentSerie < schema.seriesObjetivo) {
        setMode("rest");
      } else {
        setMode("finished");
      }
    }
  }, [mode, schema, repCount, currentSerie]);

  // Auto-transition: gate de calibración falló 3 veces consecutivas →
  // pantalla terminal con "Cancelar sesión". No ofrecemos skip silencioso
  // porque el baseline es crítico para la clasificación 3-level de squat.
  useEffect(() => {
    if (mode === "calibracion" && calibracionIntentos >= 3) {
      setMode("calibracion_fallida");
    }
  }, [mode, calibracionIntentos]);

  // Handler único para "Empezar serie" (idle) y "Próxima serie" (rest).
  // En rest hay que resetear el evaluator y avanzar de serie antes del
  // countdown. En idle, schemas con baseline pasan primero por calibracion;
  // los sin baseline (rot externa) van directo al countdown.
  const onStartSerie = () => {
    if (mode === "rest") {
      // Audio: 'cue_descanso_fin' al arrancar próxima serie desde rest.
      audio.play("cue_descanso_fin");
      evaluatorRef.current?.reset();
      setEvaluation(null);
      setCurrentSerie((s) => s + 1);
      setCompletedReps((prev) => [...prev, []]);
      setMode("counting");
      setCountdownValue(3);
      return;
    }
    // mode === 'idle'. Si el schema requiere baseline y todavía no lo
    // tenemos capturado, vamos por calibracion. Si no lo requiere o ya
    // está capturado (segunda serie hipotética desde idle), saltamos.
    if (schema?.baseline && !baselineRef.current) {
      setCalibracionProgreso(0);
      setCalibracionIntentos(0);
      prevProgressRef.current = 0;
      evaluatorRef.current?.reset();
      setMode("calibracion");
      return;
    }
    setMode("counting");
    setCountdownValue(3);
  };

  const onCancelarCalibracion = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/");
  };

  // Persistencia al cerrar sesión.
  const {
    mutate: guardarSesion,
    loading: guardando,
    error: errorGuardar,
    success: guardado,
  } = useGuardarSesionCompletada();

  const persistirSesion = () => {
    if (!schema) return;

    const fpsValues = fpsHistoryRef.current;
    const fpsAvg =
      fpsValues.length > 0
        ? fpsValues.reduce((a, b) => a + b, 0) / fpsValues.length
        : 0;
    const tiempoInicioMs = tiempoInicioRef.current;
    const durationSec = tiempoInicioMs
      ? Math.round((Date.now() - tiempoInicioMs) / 1000)
      : 0;

    const flatReps = completedReps.flat();
    const validReps = flatReps.filter((r) => r.valid);
    const qualities = validReps.map((r) => r.quality);
    const scoreAvg =
      qualities.length > 0
        ? qualities.reduce((a, b) => a + b, 0) / qualities.length
        : null;
    const scoreMin = qualities.length > 0 ? Math.min(...qualities) : null;
    const scoreMax = qualities.length > 0 ? Math.max(...qualities) : null;

    const poseAnalysis = buildPoseAnalysis({
      repsBySerie: completedReps,
      detector: {
        schemaId: schema.id,
        schemaVersion: schema.version,
        // Cada evaluator declara su propia versión (interface Evaluator).
        // Antes hardcodeábamos EVALUATOR_VERSION del legacy → mentíamos
        // la versión cuando la sesión usaba SquatEvaluator.
        evaluatorVersion: evaluatorRef.current?.version ?? "unknown",
      },
      captura: {
        userAgent: navigator.userAgent,
      },
    });

    void guardarSesion({
      sesion_id: sessionId,
      total_reps: validReps.length,
      score_promedio: scoreAvg !== null ? Number(scoreAvg.toFixed(2)) : null,
      score_minimo: scoreMin,
      score_maximo: scoreMax,
      fps_avg: Number(fpsAvg.toFixed(2)),
      duration_sec: durationSec,
      pose_analysis: poseAnalysis,
    });
  };

  // Auto-disparo de persistencia al transicionar a 'finished'.
  // Se llama UNA vez (mode no se mueve de finished). Si falla, el usuario
  // dispara retry manual via onReintentar (no via este effect).
  useEffect(() => {
    if (mode !== "finished") return;
    if (guardando || guardado) return;
    persistirSesion();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  /** Arma el resumen para SesionCompletada. Solapa parte del cómputo con
   *  persistirSesion() (durationSec, validReps, scoreAvg) — es chico, lo
   *  duplicamos antes de extraer un helper común. La diferencia es que
   *  acá computamos topAMejorar para la UI; el RPC usa la lista top-3. */
  const buildResumen = (): ResumenSesion | null => {
    if (!schema) return null;

    const tiempoInicioMs = tiempoInicioRef.current;
    const durationSec = tiempoInicioMs
      ? Math.round((Date.now() - tiempoInicioMs) / 1000)
      : 0;

    const flatReps = completedReps.flat();
    const validReps = flatReps.filter((r) => r.valid);
    const qualities = validReps.map((r) => r.quality);
    const qualityPromedio =
      qualities.length > 0
        ? qualities.reduce((a, b) => a + b, 0) / qualities.length
        : null;

    // Violación más frecuente: cuenta cuántos reps marcaron cada violationId
    // y agarra el de mayor count. % se computa sobre el total de reps
    // detectados (válidos + inválidos), no solo válidos.
    const counts = new Map<string, number>();
    for (const r of flatReps) {
      for (const v of r.violations) {
        counts.set(v, (counts.get(v) ?? 0) + 1);
      }
    }
    let topId: string | null = null;
    let topCount = 0;
    for (const [id, count] of counts) {
      if (count > topCount) {
        topId = id;
        topCount = count;
      }
    }
    const totalReps = flatReps.length;
    const topAMejorar =
      topId && totalReps > 0
        ? {
            summaryLabel: getViolationLabel(topId, schema),
            frecuenciaPct: Math.round((topCount / totalReps) * 100),
          }
        : null;

    return {
      ejercicioNombre: schema.nombre,
      durationSec,
      repsValidos: validReps.length,
      repsObjetivo: schema.repsObjetivo * schema.seriesObjetivo,
      qualityPromedio,
      topAMejorar,
      // SesionCamara siempre pasa null — el dolor lo reporta el paciente
      // en SesionCompletada via EscalaDolor. Si el paciente vuelve a la
      // pantalla post-confirmar, useResumenSesion (refresh-safe) lee el
      // valor real desde DB.
      dolorReportado: null,
    };
  };

  const loadingState: LoadingState = error
    ? "permission-error"
    : isLoading
      ? "loading"
      : "ready";

  // ==========================================================================
  // Render: schema no encontrado
  // ==========================================================================
  if (!schema) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl mb-4">Ejercicio no encontrado</h1>
          <button
            onClick={() => {
              if (window.history.length > 1) navigate(-1);
              else navigate("/");
            }}
            className="text-primary underline"
          >
            Volver
          </button>
        </div>
      </div>
    );
  }

  // ==========================================================================
  // Render: pre-session gate (necesario para que getUserMedia tenga user gesture
  // en el primer acceso, sobre todo en Safari)
  // ==========================================================================
  if (!started) {
    return (
      <>
        <div className="min-h-screen bg-background flex items-center justify-center p-6">
          {/* Card: mobile = columna centrada (max-w-md), desktop lg = layout
              2 columnas con hero visual (thumbnail YouTube clickeable → abre
              modal) a la izquierda + info + CTAs a la derecha. */}
          <div className="w-full max-w-md lg:max-w-3xl bg-card border border-border rounded-2xl p-6 lg:p-8 shadow-sm text-center lg:text-left relative">
            <button
              onClick={() => setSetupGuideOpen(true)}
              className="absolute top-3 right-3 rounded-full p-1.5 hover:bg-muted transition-colors text-muted-foreground hover:text-foreground z-10"
              aria-label="Abrir guía de configuración"
              title="¿Cómo me preparo?"
            >
              <HelpCircle className="w-5 h-5" />
            </button>

            <div className="lg:grid lg:grid-cols-[minmax(0,320px)_1fr] lg:gap-8 lg:items-center">
              {/* Hero visual — solo desktop. Thumbnail clickeable si hay
                  video_url; fallback graceful con PlayCircle si no. */}
              <div className="hidden lg:block">
                {schema.video_url ? (
                  <button
                    type="button"
                    onClick={() => setVideoTutorialOpen(true)}
                    className="group relative w-full block rounded-xl overflow-hidden focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                    aria-label="Ver video tutorial del ejercicio"
                  >
                    <YoutubeThumbnail
                      videoUrl={schema.video_url}
                      exerciseName={schema.nombre}
                    />
                    <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                      <div className="rounded-full bg-white/90 group-hover:bg-white p-3 shadow-lg transition-all group-hover:scale-105">
                        <PlayCircle
                          className="w-8 h-8 text-primary"
                          strokeWidth={1.75}
                        />
                      </div>
                    </div>
                  </button>
                ) : (
                  <YoutubeThumbnail
                    videoUrl={undefined}
                    exerciseName={schema.nombre}
                  />
                )}
              </div>

              {/* Info + CTAs — mobile vertical stack, desktop columna derecha */}
              <div>
                <h1 className="text-2xl lg:text-3xl font-semibold mb-2">
                  {schema.nombre}
                </h1>
                <p className="text-muted-foreground mb-6 text-sm lg:text-base leading-relaxed">
                  {schema.descripcion}
                </p>

                {/* Botón "Ver cómo se hace" — solo mobile (en desktop el
                    thumbnail hero es el CTA para el video). */}
                {schema.video_url && (
                  <button
                    onClick={() => setVideoTutorialOpen(true)}
                    className="lg:hidden w-full mb-2 border border-border bg-card py-2.5 rounded-full text-sm font-medium flex items-center justify-center gap-2 hover:bg-muted transition-colors"
                  >
                    <PlayCircle className="w-4 h-4" strokeWidth={1.75} />
                    Ver cómo se hace
                  </button>
                )}

                <button
                  onClick={() => {
                    // Preload audio en el user gesture (satisface auto-play
                    // policy iOS/Safari + evita latencia del primer cue).
                    audio.preload();
                    setStarted(true);
                  }}
                  className="w-full bg-primary text-primary-foreground py-3 lg:py-4 lg:text-base rounded-full font-semibold transition-all duration-200 hover:scale-[1.02]"
                >
                  Empezar sesión
                </button>
                <button
                  onClick={() => {
                    if (window.history.length > 1) navigate(-1);
                    else navigate("/");
                  }}
                  className="w-full mt-2 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
        <SetupGuideCarrusel
          open={setupGuideOpen}
          onClose={() => {
            markSetupGuideAsSeen();
            setSetupGuideOpen(false);
          }}
        />
        <VideoTutorialModal
          videoUrl={schema.video_url}
          exerciseName={schema.nombre}
          open={videoTutorialOpen}
          onClose={() => setVideoTutorialOpen(false)}
        />
      </>
    );
  }

  // ==========================================================================
  // Render: durante la sesión
  // ==========================================================================
  return (
    <SesionCamaraUI
      videoRef={videoRef}
      canvasRef={canvasRef}
      containerRef={containerRef}
      ejercicioNombre={schema.nombre}
      repCount={repCount}
      repsObjetivo={schema.repsObjetivo}
      serieActual={currentSerie}
      seriesObjetivo={schema.seriesObjetivo}
      feedback={feedback}
      fps={pose?.fps ?? 0}
      debugMode={showDebug}
      loadingState={loadingState}
      mode={mode}
      countdownValue={countdownValue}
      calibracionProgreso={calibracionProgreso}
      calibracionIntentos={calibracionIntentos}
      onToggleDebug={() => setShowDebug((s) => !s)}
      audioMuted={audio.muted}
      onToggleMuted={() => audio.setMuted(!audio.muted)}
      onSalir={() => {
        if (window.history.length > 1) navigate(-1);
        else navigate("/");
      }}
      onStartSerie={onStartSerie}
      onCancelarCalibracion={onCancelarCalibracion}
      onFinalizar={() =>
        navigate(`/sesion-completada/${sessionId}`, {
          state: buildResumen(),
        })
      }
      guardando={guardando}
      guardado={guardado}
      errorGuardar={errorGuardar}
      onReintentar={persistirSesion}
      onRetryPermission={start}
      cameraOverlay={
        showDebug ? (
          <DetectorDebugHUD
            evaluation={evaluation}
            fps={pose?.fps ?? 0}
            schema={schema}
            completedRepsBySerie={completedReps}
            currentSerie={currentSerie}
          />
        ) : null
      }
    />
  );
}
